// Pipeline — one card per client, at the furthest thing that is TRUE (Part B).
//
// Built from facts in the worker (lib/clientStage.ts): the order's status, its
// albums, the money recorded, and the messages WhatsApp accepted. Nothing the
// agent or the model said moves a card. Flags are chips (the AI's reading is
// labelled as such), never a column.
//
// ⚠️ AMBER, NOT RED, FOR « STUCK ». Red is reserved for « needs a human now »
// and lives in the Inbox; a card stuck on our side is also an Inbox row there.
// Here it is a quieter marker: « slower than it should be ».
import { useMemo, useRef, useState } from 'react';
import { useT } from '@/lib/i18n';
import type { FactChip, PipelineClient } from '@/lib/api';
import { adminOrderHref } from '@/lib/orderHref';
import { usePilotage } from './usePilotage';
import { FactChips } from './InboxTab';
import { displayName, inPeriod, splitByPeriod, waitLabel } from './workbench';
import type { DateRange } from '@/components/admin/DateRangePicker';
import { groupPipeline, LEAD_FRESH_DAYS, type PhaseGroup } from './pipelineGroups';
import { activeOrders } from './numbers';
import { useIsPhone } from './phone';
import { useFillHeight } from './fillHeight';
import { inEdge, lockAxis } from './swipe';
import { PilotageHistory } from './PilotageHistory';

/** No period given = every client (the board before the timeline). */
const ALL_TIME: DateRange = {};

const MAD = (n: number) => new Intl.NumberFormat('fr-FR').format(Math.round(n));

const minutesSince = (iso: string | null) => {
  const t = Date.parse(String(iso ?? ''));
  return Number.isFinite(t) ? (Date.now() - t) / 60_000 : null;
};
const matches = (c: PipelineClient, q: string) => {
  if (!q) return true;
  const needle = q.toLowerCase().trim();
  const digits = needle.replace(/\D/g, '');
  const hay = [c.name, c.primary?.name, ...c.otherOrders.map((o) => o.name), ...c.cancelledOrders.map((o) => o.name)]
    .filter(Boolean).join(' ').toLowerCase();
  if (hay.includes(needle)) return true;
  return digits.length >= 4 && String(c.phone ?? '').replace(/\D/g, '').includes(digits);
};

const BADGE: Record<string, { label: string; cls: string }> = {
  returning: { label: 'Revient', cls: 'border-brand-300 bg-brand-50 text-brand-800' },
  complaint: { label: 'Réclamation', cls: 'border-error/40 bg-error/10 text-error' },
  paused: { label: 'Un humain a la main', cls: 'border-border-default bg-bg-elevated text-text-secondary' },
  cancelled: { label: 'Annulé', cls: 'border-border-default bg-bg-elevated text-text-tertiary' },
};
const BALL: Record<string, string> = {
  ours: 'à nous', theirs: 'au client', courier: 'au livreur', nobody: '',
};

/**
 * What a card says about its facts: every gap (!) and note (i), then only the
 * NEXT missing step — « — Couverture — Photos — Album — Aperçu » on a new order
 * was the whole road ahead, not information.
 */
/** The payment line matters once the book is approved; before, on a board of mostly cash-on-delivery, it is noise. */
const PAYMENT_MATTERS = new Set(['approved', 'paid', 'printing', 'shipped', 'delivered']);

function cardChips(c: PipelineClient, t: (s: string, v?: Record<string, string | number>) => string): FactChip[] {
  const loud = c.chips.filter((x) => (x.state === 'warn' || x.state === 'info')
    && (x.key !== 'payment' || PAYMENT_MATTERS.has(String(c.stage))));
  const next = c.chips.find((x) => x.state === 'todo');
  const done = c.chips.filter((x) => x.state === 'done').length;
  return [
    ...loud.slice(0, 2),
    ...(next ? [next] : []),
    ...(done ? [{ key: 'done', label: t('{n} fait(s)', { n: done }), state: 'done' as const }] : []),
  ].slice(0, 3);
}

function ClientCardView({ c, onOpen }: { c: PipelineClient; onOpen: () => void }) {
  const { t } = useT();
  const chips = cardChips(c, t);
  const age = minutesSince(c.sinceIso);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full text-left rounded-lg border bg-bg-card px-3 py-2.5 border-l-[3px] cursor-pointer transition-[background-color,box-shadow] duration-150 hover:bg-bg-elevated hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
        c.stuck ? 'border-warning/50 border-l-warning' : 'border-border-subtle border-l-border-subtle'
      }`}
    >
      <span className="flex items-baseline gap-1.5 min-w-0">
        <b className="text-[12.5px] font-semibold text-text-primary truncate">{t(displayName(c.name))}</b>
        {c.money > 0 && <span className="ml-auto text-[11px] text-text-secondary tabular-nums shrink-0">{MAD(c.money)}</span>}
      </span>
      <span className="flex items-center gap-1.5 mt-0.5 text-[11px] text-text-tertiary min-w-0">
        {c.primary?.name && <span className="tabular-nums shrink-0">{c.primary.name}</span>}
        {age != null && <span className="shrink-0">· {waitLabel(age)}</span>}
        {c.ballWith !== 'nobody' && <span className={`truncate ${c.stuck ? 'text-[#8F5F2A] dark:text-status-waiting font-medium' : ''}`}>· {c.stuck ? t('bloqué') + ' ' : ''}{t(BALL[c.ballWith])}</span>}
      </span>
      {chips.length > 0 && (
        <span className="block mt-1.5">
          <FactChips compact chips={chips} />
        </span>
      )}
      {c.badges.length > 0 && (
        <span className="flex flex-wrap gap-1 mt-1.5">
          {c.badges.map((b) => (
            <span key={b} className={`rounded-full border px-1.5 py-px text-[10.5px] ${BADGE[b]?.cls ?? ''}`}>{t(BADGE[b]?.label ?? b)}</span>
          ))}
        </span>
      )}
    </button>
  );
}

export function PipelineTab({ onOpenChat, range = ALL_TIME, onShowAll, stuckOnly = false, onStuckOnly }: {
  onOpenChat: (chatId: string) => void;
  /** The page's timeline (owner 2026-09-26: 7 days by default). */
  range?: DateRange;
  onShowAll?: () => void;
  /** Only the orders stuck on OUR side — where the numbers strip's first card lands (`?sub=stuck`). */
  stuckOnly?: boolean;
  onStuckOnly?: (on: boolean) => void;
}) {
  const { t } = useT();
  const isPhone = useIsPhone();
  const { data, isLoading, isError } = usePilotage();
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [unfolded, setUnfolded] = useState<Set<string>>(() => new Set());
  // « Lanes fill the screen; each scrolls on its own » (owner, 2026-10-07).
  const [boardRef, boardH] = useFillHeight(!isPhone);

  const pipeline = data?.pipeline;
  // ⚠️ THE PERIOD: clients ACTIVE in it (last activity — the server's rule for
  // the board). A search looks everywhere; what the period hides is counted.
  const period = useMemo(
    () => splitByPeriod(pipeline?.clients ?? [], (c) => c.lastActivityIso, range, () => Boolean(q)),
    [pipeline, range, q],
  );
  const clients = useMemo(() => period.shown
    .filter((c) => (showAll || q ? true : c.active && !c.ignored))
    .filter((c) => matches(c, q))
    .filter((c) => !stuckOnly || (c.stuck && c.ballWith === 'ours' && activeOrders([c]).length > 0)), [period, showAll, q, stuckOnly]);
  const outsideCount = period.outside > 0
    ? (pipeline?.clients ?? []).filter((c) => !inPeriod(c.lastActivityIso, range) && (showAll || (c.active && !c.ignored))).length
    : 0;

  if (isLoading) return <p className="text-[12.5px] text-text-tertiary">{t('Chargement…')}</p>;
  if (isError || !data) return <p className="text-[12.5px] text-[#8F5F2A] dark:text-status-waiting">{t('Impossible de charger le pipeline.')}</p>;
  if (!pipeline) return <p className="text-[12.5px] text-text-tertiary">{t('Le pipeline arrive avec la nouvelle version du serveur.')}</p>;

  // One grouping for the board and the phone list (pipelineGroups.ts).
  const { phases: groups, offBoard } = groupPipeline(clients, pipeline.phases, { unfolded, searching: Boolean(q) });

  // Orders being made (money on the table) vs people still talking — two numbers, not one.
  const active = activeOrders(clients);
  const inProgress = active.reduce((s, c) => s + c.money, 0);
  const stuckOurs = active.filter((c) => c.stuck && c.ballWith === 'ours').length;
  const freshCutoff = Date.now() - LEAD_FRESH_DAYS * 86_400_000;
  const recentLeads = clients.filter((c) => (c.stage === 'new_lead' || c.stage === 'talking')
    && (Date.parse(String(c.lastActivityIso ?? '')) || 0) >= freshCutoff).length;
  const open = openKey ? (pipeline.clients.find((c) => c.key === openKey) ?? null) : null;
  const label = (key: string) => t(pipeline.stages.find((s) => s.key === key)?.label ?? key);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-[22px] leading-tight text-text-primary">{t('Pipeline')}</h2>
          {/* On a phone the numbers strip above says this; desktop keeps its line. */}
          <p className="hidden md:block text-[12px] text-text-secondary mt-0.5 tabular-nums">
            {t('{n} commandes en cours · {m} MAD · {s} bloquées chez nous · {l} contacts', { n: active.length, m: MAD(inProgress), s: stuckOurs, l: recentLeads })}
          </p>
          {outsideCount > 0 && (
            <p className="text-[11.5px] text-text-tertiary mt-0.5 tabular-nums">
              {t('{n} client(s) hors de cette période', { n: outsideCount })}
              {onShowAll && (<>
                {' · '}
                <button type="button" onClick={onShowAll} className="underline underline-offset-2 hover:text-text-primary cursor-pointer">
                  {t('Tout afficher')}
                </button>
              </>)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full md:w-auto">
          <label className="sr-only" htmlFor="pipe-q">{t('Chercher un client')}</label>
          <input
            id="pipe-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Nom, commande ou téléphone')}
            className="h-11 md:h-10 w-full md:w-64 max-w-full px-3 rounded-lg border border-border-default bg-bg-card text-[16px] md:text-[13px] text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <label className="inline-flex items-center gap-2 min-h-[44px] md:min-h-0 text-[12px] text-text-secondary cursor-pointer select-none">
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="accent-brand-500 w-4 h-4" />
            {t('Annulés et ignorés')}
          </label>
          <PilotageHistory variant="drawer" />
        </div>
      </div>

      {stuckOnly && (
        <p className="flex items-center gap-2 flex-wrap text-[12.5px] text-[#8F5F2A] dark:text-status-waiting" role="status">
          <span className="w-2 h-2 rounded-full bg-warning" aria-hidden />
          <span className="font-semibold">{t('Bloquées chez nous')} · {clients.length}</span>
          {onStuckOnly && (
            <button type="button" onClick={() => onStuckOnly(false)}
              className="min-h-[44px] md:min-h-0 px-1 underline underline-offset-2 text-text-secondary hover:text-text-primary cursor-pointer">
              {t('Tout le pipeline')}
            </button>
          )}
        </p>
      )}

      {isPhone
        ? <PhoneList groups={stuckOnly || q ? groups.filter((g) => g.count > 0) : groups} label={label} onOpen={setOpenKey} onUnfold={(s) => setUnfolded((u) => new Set(u).add(s))} />
        : (
      /* ─── THE BOARD — phases over their stage columns ─────────────────
         Each lane: its name + count on top, its cards scrolling under it. */
      <div ref={boardRef} data-board style={boardH != null ? { height: boardH } : undefined}
        className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-2">
        <div className="flex gap-4 min-w-max h-full">
          {groups.map((p) => (
            <section key={p.key} className="flex flex-col gap-2 min-h-0">
              <h3 className="shrink-0 text-[12px] font-semibold text-text-secondary px-0.5">{t(p.label)}</h3>
              <div className="flex gap-2.5 flex-1 min-h-0">
                {p.stages.map(({ key: s, list, shown, money }) => (
                    <div key={s} data-lane={s} className="w-[196px] shrink-0 rounded-xl bg-bg-elevated/70 border border-border-subtle flex flex-col min-h-0 max-h-full">
                      <div className="shrink-0 px-3 pt-2.5 pb-1.5 border-b border-border-subtle/60">
                        <p className="flex items-baseline justify-between gap-2">
                          <span className="text-[12.5px] font-semibold text-text-primary">{label(s)}</span>
                          <span className="text-[11.5px] text-text-tertiary tabular-nums">{list.length}</span>
                        </p>
                        {money > 0 && <p className="text-[11px] text-text-tertiary tabular-nums">{MAD(money)} MAD</p>}
                      </div>
                      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2 flex flex-col gap-2" data-no-ptr>
                        {shown.map((c) => <ClientCardView key={c.key} c={c} onOpen={() => setOpenKey(c.key)} />)}
                        {list.length > shown.length && (
                          <button type="button" onClick={() => setUnfolded((u) => new Set(u).add(s))}
                            className="text-[11.5px] text-text-secondary underline underline-offset-2 cursor-pointer py-1">
                            {t('+ {n} plus anciens', { n: list.length - shown.length })}
                          </button>
                        )}
                        {!list.length && <p className="text-[11px] text-text-tertiary px-1 pb-1">{t('Personne')}</p>}
                      </div>
                    </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
        )}

      {offBoard.length > 0 && (
        <section>
          <h3 className="text-[12px] font-semibold text-text-secondary mb-2">{t('Hors du tableau — commandes annulées')} · {offBoard.length}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2.5">
            {offBoard.map((c) => <ClientCardView key={c.key} c={c} onOpen={() => setOpenKey(c.key)} />)}
          </div>
        </section>
      )}

      {open && <ClientDrawer c={open} onClose={() => setOpenKey(null)} onOpenChat={onOpenChat} stageLabel={label} />}
    </div>
  );
}

/**
 * The Pipeline on a phone (app-feel plan, builder B): one vertical list — a
 * phase heading, then each stage that has someone, its count and money, its
 * cards full width. No sideways scroll. The same folding as the board.
 */
function PhoneList({ groups, label, onOpen, onUnfold }: {
  groups: PhaseGroup[]; label: (k: string) => string; onOpen: (key: string) => void; onUnfold: (stage: string) => void;
}) {
  const { t } = useT();
  // One lane at a time (owner, 2026-10-07): the first with someone in it.
  const [picked, setPicked] = useState<string | null>(null);
  const activeKey = picked && groups.some((g) => g.key === picked)
    ? picked : (groups.find((g) => g.count > 0)?.key ?? groups[0]?.key ?? null);
  const idx = groups.findIndex((g) => g.key === activeKey);
  const p = idx >= 0 ? groups[idx] : null;
  const start = useRef<{ x: number; y: number } | null>(null);
  const step = (dir: 1 | -1) => {
    const next = groups[idx + dir];
    if (next) setPicked(next.key);
  };
  if (!p) return null;
  const filled = p.stages.filter((s) => s.count > 0);
  return (
    <div className="space-y-3">
      <nav role="tablist" aria-label={t('Phases')} className="flex flex-wrap gap-2">
        {groups.map((g) => (
          <button key={g.key} type="button" role="tab" aria-selected={g.key === activeKey} data-phase-tab={g.key}
            onClick={() => setPicked(g.key)}
            className={`inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-full border text-[12.5px] cursor-pointer ${
              g.key === activeKey ? 'border-brand-400 bg-brand-50 text-brand-800' : 'border-border-subtle bg-bg-card text-text-secondary'
            }`}>
            {t(g.label)} <b className="font-semibold tabular-nums">{g.count}</b>
          </button>
        ))}
      </nav>
      <section key={p.key} data-phase={p.key} role="tabpanel" className="storiz-fade-in"
        onTouchStart={(e) => { const x = e.touches[0]; start.current = inEdge(x.clientX) ? null : { x: x.clientX, y: x.clientY }; }}
        onTouchEnd={(e) => {
          const s0 = start.current; start.current = null;
          if (!s0) return;
          const x = e.changedTouches[0];
          const dx = x.clientX - s0.x; const dy = x.clientY - s0.y;
          if (lockAxis(dx, dy) === 'x' && Math.abs(dx) > 60) step(dx < 0 ? 1 : -1);
        }}>
        <h3 className="flex items-baseline justify-between font-heading text-[18px] text-text-primary mb-2">
          {t(p.label)}
          <span className="font-sans text-[12px] text-text-tertiary tabular-nums">{p.count}</span>
        </h3>
        {!filled.length && <p className="text-[12px] text-text-tertiary">{t('Personne')}</p>}
        <div className="space-y-4">
          {filled.map((s) => (
            <div key={s.key} data-stage={s.key}>
              <p className="sticky top-0 z-[1] bg-bg-primary flex items-baseline gap-2 text-[13px] font-semibold text-text-primary mb-1.5 px-0.5 py-1">
                {label(s.key)}
                <span data-count className="text-[12px] font-normal text-text-tertiary tabular-nums">{s.count}</span>
                {s.money > 0 && <span className="ml-auto text-[12px] font-normal text-text-tertiary tabular-nums">{MAD(s.money)} MAD</span>}
              </p>
              <div className="space-y-2">
                {s.shown.map((c) => <ClientCardView key={c.key} c={c} onOpen={() => onOpen(c.key)} />)}
              </div>
              {s.list.length > s.shown.length && (
                <button type="button" onClick={() => onUnfold(s.key)}
                  className="mt-1 min-h-[44px] text-[12.5px] text-text-secondary underline underline-offset-2 cursor-pointer">
                  {t('+ {n} plus anciens', { n: s.list.length - s.shown.length })}
                </button>
              )}
            </div>
          ))}
        </div>
        {groups.length > 1 && (
          <p className="mt-4 text-center text-[11.5px] text-text-tertiary">{t('Glissez à gauche ou à droite pour la phase suivante.')}</p>
        )}
      </section>
    </div>
  );
}

function ClientDrawer({ c, onClose, onOpenChat, stageLabel }: {
  c: PipelineClient; onClose: () => void; onOpenChat: (chatId: string) => void; stageLabel: (k: string) => string;
}) {
  const { t } = useT();
  const age = minutesSince(c.sinceIso);
  const orderHref = c.primary ? adminOrderHref({ shopifyOrderId: c.primary.shopifyOrderId }) : null;
  return (
    <div className="fixed inset-0 z-50 bg-text-primary/30 flex justify-end" onClick={onClose}>
      <aside role="dialog" aria-modal="true" aria-label={t(displayName(c.name))}
        className="w-full max-w-md h-full bg-bg-card border-l border-border-subtle overflow-y-auto storiz-fade-in" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 pt-5 pb-3 border-b border-border-subtle">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-heading text-[21px] leading-tight text-text-primary">{t(displayName(c.name))}</h3>
              <p className="text-[12px] text-text-secondary mt-1">
                {c.stageLabel}{age != null ? ` · ${t('depuis {w}', { w: waitLabel(age) })}` : ''}
                {c.money > 0 ? ` · ${MAD(c.money)} MAD` : ''}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-[12.5px] text-text-secondary cursor-pointer shrink-0">{t('Fermer')}</button>
          </div>
        </header>
        <div className="px-5 py-4 space-y-5">
          <FactChips chips={c.chips} />
          <div className="flex flex-wrap gap-2">
            {c.chatId && (
              <button type="button" onClick={() => { onOpenChat(c.chatId!); onClose(); }}
                className="h-10 px-4 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold cursor-pointer transition-colors duration-150">
                {t('Ouvrir la conversation')}
              </button>
            )}
            {orderHref && (
              <a href={orderHref} className="h-10 px-4 inline-flex items-center rounded-lg border border-border-default text-[13px] text-text-primary hover:bg-bg-elevated">
                {t('Voir la commande')} {c.primary?.name}
              </a>
            )}
          </div>
          {c.otherOrders.length > 0 && (
            <div>
              <p className="text-[12px] font-semibold text-text-secondary mb-1.5">{t('Ses autres commandes')}</p>
              <ul className="space-y-1 text-[12.5px]">
                {c.otherOrders.map((o) => (
                  <li key={o.id} className="flex justify-between gap-3">
                    <span className="text-text-primary tabular-nums">{o.name ?? '—'}</span>
                    <span className="text-text-secondary">{stageLabel(o.stage)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {c.cancelledOrders.length > 0 && (
            <p className="text-[12px] text-text-tertiary">
              {t('Annulées : {list}', { list: c.cancelledOrders.map((o) => o.name ?? '—').join(', ') })}
            </p>
          )}
          <p className="text-[11.5px] text-text-tertiary leading-snug">
            {t('Tout ce qui est ici vient de la commande, des albums, de l’argent enregistré et des messages que WhatsApp a acceptés. Ce que l’IA a dit ou deviné n’avance jamais un client.')}
          </p>
        </div>
      </aside>
    </div>
  );
}
